import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { Dollar } from "components/icons/Dollar";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import { PaperPencil } from "components/icons/PaperPencil";
import { PaperWithCross } from "components/icons/PaperWithCross";
import { PaperWithTick } from "components/icons/PaperWithTick";
import { PersonWithClock } from "components/icons/PersonWithClock";
import React from "react";
import { FilingStateTypes, TaskDeadlineState } from "types/Models/task";
import { TaskStatusGuide } from "./TaskStatusGuide";
import { useModal } from "hooks/useModal";
import { categorizeFilingState } from "utils/categorizeFilingState";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import {
  TIMELINE_STATUS,
  TIMELINE_STATUS_TAG_TYPE,
} from "constants/filingsTimeline";

type SvgProps = React.SVGProps<SVGSVGElement>;

export const TaskStateNameMap: Record<string, string> = {
  PAYMENT: "Pending payment",
  FORM_FILING: "Add details",
  FILING_IN_PROGRESS: "Filing in progress",
  SUBMITTED: "Filing completed",
  TASK_AMENDMENT: "Amendment in progress",
  TASK_AMENDMENT_COMPLETED: "Amendment filed",
};

export const TaskDescription: Record<string, string> = {
  PAYMENT:
    "This filing hasn’t been purchased yet. Please complete payment to begin your filing process.",
  FORM_FILING:
    "We need a few details from you before we can proceed. Please review and complete the pending information. ",
  FILING_IN_PROGRESS:
    "Your filing is currently in progress. Our team is preparing your draft and will update you once it’s ready.",
  SUBMITTED:
    "Your filing has been successfully completed. You can view the filed copy in your dashboard.  ",
  TASK_AMENDMENT:
    "An amendment for your filing is underway. Our team is updating your details and will share progress soon. ",
  TASK_AMENDMENT_COMPLETED:
    "The amendment has been successfully completed. You can access the updated filing details in your dashboard.",
};
export const TaskStateTagTypes: Record<string, TagColorType> = {
  PAYMENT: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.UNPAID],
  FORM_FILING: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.PENDING_ON_YOU],
  FILING_IN_PROGRESS: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  SUBMITTED: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.COMPLETED],
  TASK_AMENDMENT: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  TASK_AMENDMENT_COMPLETED: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.COMPLETED],
};

const TaskStateStatuses = {
  pending: "Upcoming",
  completed: "Completed",
  inProgress: "Ongoing",
};

export const ClientStageTagTypes: Record<string, TagColorType> = {
  PRICING: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.UNPAID],
  ADD_DETAILS: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.PENDING_ON_YOU],
  IN_QUEUE: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  PREPARER_ASSIGNED: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  PREP_IN_PROGRESS: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  MISSING_INFORMATION: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.PENDING_ON_YOU],
  REVIEW_IN_PROGRESS: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  DRAFT_SHARED: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  READY_TO_FILE: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  COMPLETED: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.COMPLETED],
  AMENDMENT: TIMELINE_STATUS_TAG_TYPE[TIMELINE_STATUS.IN_PROGRESS],
  // On hold sits outside the timeline buckets - it needs to read as blocked.
  ON_HOLD: "red",
};

export const ClientProgressState = ({
  currentKey,
  currentLabel,
  description,
}: {
  currentKey: string;
  currentLabel: string;
  description?: string | null;
}) => (
  <div className="t-flex t-self-stretch t-items-start">
    <ToolTip
      text={
        description ? (
          <div className="t-text-body-sm t-space-y-2">
            <span className="t-font-bold t-text-white">{currentLabel}</span>
            <br />
            <span className="t-text-neutral-50">{description}</span>
          </div>
        ) : (
          currentLabel
        )
      }
      propClass="t-flex t-justify-center t-flex-col t-text-body-sm t-text-neutral-50 t-w-72 !t-text-left"
    >
      <span>
        <Tag
          size="small"
          rounded
          tagType={ClientStageTagTypes[currentKey] || "blue"}
          transparent
        >
          {currentLabel}
        </Tag>
      </span>
    </ToolTip>
  </div>
);

export const TaskStates = ({
  states,
  currentStateType,
}: {
  states: TaskDeadlineState[];
  currentStateType: FilingStateTypes;
}) => {
  const statusGuide = useModal();
  const categorizedCurrentState = categorizeFilingState(currentStateType);

  return (
    <div className="t-flex  t-self-stretch t-items-start">
      <ToolTip
        text={
          <div className="t-text-body-sm t-space-y-2">
            <span className="t-font-bold t-text-white">
              {TaskStateNameMap[categorizedCurrentState]}
            </span>
            <br />
            <span className="t-text-neutral-50">
              {TaskDescription[categorizedCurrentState]}
            </span>
          </div>
        }
        propClass="t-flex t-justify-center t-flex-col t-text-body-sm t-text-neutral-50 t-w-72 !t-text-left"
      >
        <span>
          <Tag
            size="small"
            rounded
            tagType={TaskStateTagTypes[categorizedCurrentState]}
            transparent
          >
            {TaskStateNameMap[categorizedCurrentState]}
          </Tag>
        </span>
      </ToolTip>
      <TaskStatusGuide {...statusGuide} />
    </div>
  );
};
